import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const UFS = ["AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA","PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"];

/** Criar (group = null) ou editar uma turma. O professor pode estar com acesso pendente.
 *  O nome da turma é sugerido pelo nome do professor (+ estado, opcional). */
export const GroupFormDialog = ({ open, onOpenChange, group, onSaved }) => {
  const editing = !!group;
  const [form, setForm] = useState({ name: "", description: "", instructor_person_id: "none", representative_person_id: "none", contract_external: false });
  const [instructors, setInstructors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uf, setUf] = useState("");
  const [autoName, setAutoName] = useState(true); // nome segue o professor até a pessoa editar à mão

  const suggested = (pid, state) => {
    const prof = instructors.find((i) => i.id === pid);
    return prof ? `${prof.name}${state ? ` - ${state}` : ""}` : "";
  };
  const pickProfessor = (pid) => setForm((p) => ({ ...p, instructor_person_id: pid, name: autoName && pid !== "none" ? suggested(pid, uf) : p.name }));
  const pickUf = (state) => { setUf(state); if (autoName) setForm((p) => ({ ...p, name: suggested(p.instructor_person_id, state) || p.name })); };

  useEffect(() => {
    if (!open) return;
    api.get("/instructors").then((r) => setInstructors(r.data)).catch(() => setInstructors([]));
    setUf("");
    setAutoName(!group);
    setForm({
      name: group?.name || "",
      description: group?.description || "",
      instructor_person_id: group?.instructor_person_id || "none",
      representative_person_id: group?.representative_person_id || "none",
      contract_external: !!group?.contract_external,
    });
  }, [open, group]);

  // turmas antigas guardam só o usuário do professor: encontra o cadastro correspondente
  useEffect(() => {
    if (!open || !group || group.instructor_person_id || !group.instructor_id) return;
    const person = instructors.find((i) => i.user_id === group.instructor_id);
    if (person) setForm((p) => ({ ...p, instructor_person_id: person.id }));
  }, [open, group, instructors]);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Informe o nome da turma.");
    setSaving(true);
    try {
      const body = { name: form.name, description: form.description, instructor_person_id: form.instructor_person_id === "none" ? "" : form.instructor_person_id,
        representative_person_id: form.representative_person_id === "none" ? "" : form.representative_person_id,
        contract_external: !!form.contract_external };
      const res = editing ? await api.put(`/groups/${group.id}`, body) : await api.post("/groups", body);
      toast.success(editing ? "Turma atualizada!" : "Turma criada!");
      onOpenChange(false);
      onSaved?.(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">{editing ? "Editar turma" : "Nova turma"}</DialogTitle>
          <DialogDescription>Escolha o professor(a): o nome da turma é preenchido com o nome dele(a).</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Professor(a) da turma</Label>
            <Select value={form.instructor_person_id} onValueChange={pickProfessor}>
              <SelectTrigger data-testid="group-instructor-select"><SelectValue placeholder="Selecione o professor(a)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sem professor</SelectItem>
                {instructors.filter((i) => i.profile !== "representante").map((i) => (
                  <SelectItem key={i.id} value={i.id}>
                    {i.name}{i.status !== "cadastrado" ? " (acesso pendente)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {instructors.find((i) => i.id === form.instructor_person_id)?.ceo && (
            <div className="space-y-1.5">
              <Label>Representante Credenciado (opcional)</Label>
              <Select value={form.representative_person_id} onValueChange={(v) => setForm((p) => ({ ...p, representative_person_id: v }))}>
                <SelectTrigger data-testid="group-representative-select"><SelectValue placeholder="Sem representante" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sem representante</SelectItem>
                  {instructors.filter((i) => i.profile === "representante").map((i) => (
                    <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-400">O representante é quem paga a taxa de bônus das alunas desta turma e recebe a nota fiscal.</p>
            </div>
          )}
          {!editing && (
            <div className="space-y-1.5">
              <Label>Estado da turma (opcional)</Label>
              <select value={uf} onChange={(e) => pickUf(e.target.value)} className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="group-uf-select">
                <option value="">— sem estado —</option>
                {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Nome da turma *</Label>
            <Input data-testid="group-name-input" value={form.name}
              onChange={(e) => { setAutoName(false); setForm((p) => ({ ...p, name: e.target.value })); }}
              placeholder="Escolha o professor(a) ou digite o nome" />
            {!editing && autoName && form.name && <p className="text-xs text-slate-400">Nome sugerido pelo professor(a){uf ? " e estado" : ""}. Pode editar.</p>}
          </div>
          <div className="space-y-1.5">
            <Label>Descrição</Label>
            <Textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} rows={2} />
          </div>
          <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 bg-slate-50 cursor-pointer" data-testid="group-contract-external">
            <input type="checkbox" className="mt-1 h-4 w-4" checked={!!form.contract_external} onChange={(e) => setForm((p) => ({ ...p, contract_external: e.target.checked }))} />
            <span className="text-sm text-slate-700">
              <strong>Assinatura externa (Autentique, com verificação facial)</strong>
              <span className="block text-xs text-slate-500 mt-0.5">Marcado: as alunas desta turma assinam o contrato pela Autentique. Desmarcado: assinam pela assinatura da própria plataforma. Vale para quem ainda não assinou.</span>
            </span>
          </label>
          <p className="text-xs text-slate-400">Professor(a) com acesso pendente é ligado(a) à turma assim que ativar a conta.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button data-testid="group-save-button" onClick={save} disabled={saving} className="text-white" style={{ background: "#1E9E2A" }}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Salvar alterações" : "Criar turma"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
